import { Analytics } from '@cobliteam/analytics';
import Alert from '@cobliteam/ui/components/Alert';
import Button from '@cobliteam/ui/components/Button';
import Popover, { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import { Textarea } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import type { UIDataTypes, UIMessage, UITools } from 'ai';
import { useEffect, useState } from 'react';

interface DislikeFeedbackPopoverProps {
  message: UIMessage<UIDataTypes, UITools>;
  negativeFeedback: boolean;
  initialComment?: string;
  onFeedbackSent: (comment?: string) => void;
  /** Analytics source identifier */
  source?: string;
}

const DislikeFeedbackPopover = ({ message, negativeFeedback, initialComment, onFeedbackSent, source }: DislikeFeedbackPopoverProps) => {
  const [comment, setComment] = useState(initialComment || '');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (initialComment) {
      setComment(initialComment);
    }
  }, [initialComment]);

  const handleSubmit = () => {
    const trimmedComment = comment.trim() || undefined;

    Analytics.track('AI - Chat - Response Feedback Given', {
      feedbackType: 'negative',
      comment: trimmedComment,
      source,
      threadId: message.metadata?.threadId,
      traceId: message.metadata?.traceId || message.id,
    });

    Alert.success('Feedback enviado com sucesso!', { position: 'bottom-right' });
    onFeedbackSent(trimmedComment);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip content='Não gostei da resposta' asChild>
        <PopoverTrigger asChild>
          <div>
            <Button size='sm' variant={negativeFeedback ? 'contained' : 'ghost'} icon={<Icon name='thumbs-down' className='size-4' />} data-testid='ai-action-dislike' />
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align='start' className='data-[state=closed]:!zoom-out-0 data-[state=open]:!zoom-in-0 w-80 origin-bottom-left p-4 duration-400'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1'>
            <Typography variant='text-md-semibold'>Não gostou do resultado?</Typography>
            <Typography variant='text-sm' color='secondary'>
              Explique o motivo da sua insatisfação para nos ajudar a melhorar a resposta. Sua mensagem é opcional mas indicada.
            </Typography>
          </div>
          <Textarea placeholder='Sua mensagem...' value={comment} onChange={(e) => setComment(e.target.value)} className='min-h-[100px] text-sm' autoFocus />
          <div className='flex justify-end gap-2'>
            <Button variant='ghost' size='sm' onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            <Button variant='contained' color='primary' size='sm' onClick={handleSubmit}>
              Enviar
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DislikeFeedbackPopover;
